import React, { forwardRef, memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Box, IconButton, List, ListItem, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CancelIcon, FontIcon } from '@sharedCR/coreDesignSystem/icons';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { typesToExcludeFromAiExtractionModelMapping } from 'constants/documentExtractionFieldsMapping';
import { DOC_FIELD_TYPES } from 'constants/documentFieldTypes';

import { getDocumentExtractionFieldsWithNamedType } from 'store/system/selectors';

import { DocumentTestIds } from '../../document.testIds';

import {
  bodyStyles,
  cardStyles,
  closeButtonStyles,
  CONFIDENCE_HIGH_THRESHOLD,
  CONFIDENCE_MEDIUM_THRESHOLD,
  confidenceChipStyles,
  confidenceFillStyles,
  confidenceRowStyles,
  confidenceTrackStyles,
  fieldIconStyles,
  fieldNameStyles,
  formatConfidencePercent,
  headerStyles,
  pickerListStyles,
  sectionLabelStyles,
} from './BoundingBoxPopUp.styles';

const getConfidenceColor = (fraction, palette) => {
  if (fraction >= CONFIDENCE_HIGH_THRESHOLD) {
    return palette.success.main;
  }

  if (fraction >= CONFIDENCE_MEDIUM_THRESHOLD) {
    return palette.warning.main;
  }

  return palette.error.main;
};

const BoundingBoxPopUp = forwardRef(
  ({ popUpInfo = null, onClose, setAiModelFieldToFillInForm = null }, ref) => {
    const extractionFields = useSelector(getDocumentExtractionFieldsWithNamedType);
    const { palette } = useTheme();

    const {
      extractionField,
      type: aiModelFieldInfo,
      value: aiModelFieldValue,
      confidence,
      itemValues,
    } = popUpInfo || {};

    // The picker (assign the value to an extraction field) is an editing action — it renders
    // only when an onFill handler is wired. In read-only "locate in doc" mode it's omitted.
    const hasFieldPicker = !_.isNil(setAiModelFieldToFillInForm);
    const hasConfidence = !_.isNil(confidence);
    const confidenceColor = hasConfidence ? getConfidenceColor(confidence, palette) : null;

    const extractionFieldName = extractionField?.name;
    const aiModelFieldName = aiModelFieldInfo?.name;
    const boundingBoxName = extractionFieldName || aiModelFieldName;

    const adjustedExtractionFields = useMemo(() => {
      const filteredExtractionFields = extractionFields.filter(({ type, name }) => {
        const typeFiltration = extractionField?.type === type || type === DOC_FIELD_TYPES.TEXT;

        return (
          !typesToExcludeFromAiExtractionModelMapping.includes(type) &&
          name !== extractionFieldName &&
          typeFiltration
        );
      });

      if (extractionField) {
        return [extractionField, ...filteredExtractionFields];
      }

      return filteredExtractionFields;
    }, [extractionFieldName, extractionField, extractionFields]);

    const onExtractionFieldOptionClick = useCallback(
      e => {
        const { extractionFieldId } = e.currentTarget.dataset;
        setAiModelFieldToFillInForm({
          name: aiModelFieldName,
          value: aiModelFieldValue,
          type: aiModelFieldInfo?.type,
          extractionFieldId,
          itemValues,
        });
      },
      [
        setAiModelFieldToFillInForm,
        aiModelFieldName,
        aiModelFieldValue,
        aiModelFieldInfo?.type,
        itemValues,
      ],
    );

    return (
      <Box ref={ref} sx={cardStyles}>
        <Box sx={headerStyles}>
          <Box sx={fieldIconStyles}>
            <FontIcon size={IconSize[20]} />
          </Box>
          <Typography noWrap sx={fieldNameStyles} title={boundingBoxName} variant="bold14">
            {boundingBoxName}
          </Typography>
          <IconButton
            color="inherit"
            data-testid={DocumentTestIds.BOUNDING_BOX_CLOSE_BUTTON}
            sx={closeButtonStyles}
            onClick={onClose}
          >
            <CancelIcon size={IconSize[20]} />
          </IconButton>
        </Box>

        <Box sx={bodyStyles}>
          <div>
            <Typography sx={sectionLabelStyles} variant="bold12">
              Content
            </Typography>
            <Typography className="text-wrap" variant="bold16">
              {aiModelFieldValue}
            </Typography>
          </div>

          {hasConfidence && (
            <div>
              <Typography sx={sectionLabelStyles} variant="bold12">
                Confidence
              </Typography>
              <Box sx={confidenceRowStyles}>
                <Box sx={confidenceTrackStyles}>
                  <Box sx={confidenceFillStyles(confidence, confidenceColor)} />
                </Box>
                <Typography sx={confidenceChipStyles(confidenceColor)} variant="bold12">
                  {formatConfidencePercent(confidence)}
                </Typography>
              </Box>
            </div>
          )}

          {hasFieldPicker && (
            <div>
              <Typography sx={sectionLabelStyles} variant="bold12">
                Select input to fill
              </Typography>
              <List
                dense
                disablePadding
                className="overflow-y-auto"
                component="ul"
                sx={pickerListStyles}
              >
                {adjustedExtractionFields.map(({ name, id }) => (
                  <ListItem
                    button
                    data-extraction-field-id={id}
                    key={id}
                    role="listitem"
                    onClick={onExtractionFieldOptionClick}
                  >
                    {name}
                  </ListItem>
                ))}
              </List>
            </div>
          )}
        </Box>
      </Box>
    );
  },
);

BoundingBoxPopUp.propTypes = {
  onClose: PropTypes.func.isRequired,
  setAiModelFieldToFillInForm: PropTypes.func,
  boundingBoxPopUpRef: PropTypes.shape({ current: PropTypes.object }),
  popUpInfo: PropTypes.object,
};

export default memo(BoundingBoxPopUp);
